<template>
  <div
    class="oct-text-image-half"
    :data-ab-id="dataAbId"
  >
    <div class="section-left">
      <span>{{ topTitle }}</span>
      <h2 v-if="title">
        {{ title }}
      </h2>
      <p>{{ subText }}</p>
      <SharedLink
        v-if="link && ctaText"
        :link="link"
      >
        {{ ctaText }}
      </SharedLink>
    </div>
    <div class="section-right">
      <SharedLink
        :link="imgLink"
        class="inline-block"
      >
        <NuxtImg
          :src="imageSrc"
          sizes="lg:100vw 48vw"
          :srcset="imageSrcset"
          :alt="imgAlt"
          :title="imgTitle"
          :loading="imageLoadType"
          :preload="imageLoadType === 'eager'"
          :fetchpriority="imageLoadType === 'eager' ? 'high' : 'low'"
        />
      </SharedLink>
    </div>
  </div>
</template>

<script lang="ts" setup>
import magnoliaGetters from '~/composables/getters/magnoliaGetters';

interface Props {
  image?: any;
  imageTitle?: string;
  imageLink?: any[];
  alt?: string;
  topTitle?: string;
  topTitleColor?: string;
  title?: string;
  titleColor?: string;
  subText?: string;
  subTextColor?: string;
  ctaLink?: any[];
  ctaTextColor?: string;
  buttonColor?: string;
  borderColor?: string;
  imageLoadType?: HTMLImageElement['loading'];
  dataAbId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  image: () => ({}),
  imageTitle: '',
  imageLink: () => [],
  alt: '',
  topTitle: '',
  topTitleColor: '',
  title: '',
  titleColor: '',
  subText: '',
  subTextColor: '',
  ctaLink: () => [],
  ctaTextColor: '',
  buttonColor: '',
  borderColor: '',
  imageLoadType: 'lazy',
});

const {
  image, imageLink, alt, imageTitle, ctaLink,
} = toRefs(props);

const { $domain } = useNuxtApp();
const link = computed(() => (ctaLink.value?.[0] ? magnoliaGetters.getLinkAndName(ctaLink.value[0], $domain.cmsAssetDomainPath, ctaLink.value[0].switchable?.intLinkParam) : {}));
const ctaText = computed(() => (ctaLink.value?.[0] ? ctaLink.value?.[0].ctaText : ''));
const imageSrc = computed(() => (image.value ? magnoliaGetters.getDefaultSrcAsset(image.value, $domain.cmsAssetDomainPath) : ''));
const imageSrcset = computed(() => (image.value ? magnoliaGetters.getSrcSetImagesAsString(image.value, $domain.cmsAssetDomainPath, image.value.aspectRatio, image.value.mobileAspectRatio?.[0]?.aspectRatio) : []));
const imgLink = computed(() => (imageLink.value?.[0] ? magnoliaGetters.getLinkAndName(imageLink.value[0], $domain.cmsAssetDomainPath) : {}));
const imgAlt = computed(() => (magnoliaGetters.getAltImage(image.value, alt.value)));
const imgTitle = computed(() => (magnoliaGetters.getTitleImage(image.value, imageTitle.value)));
</script>

<style lang="scss" scoped>
.oct-text-image-half {
  margin: var(--spacer-base) 80px;
  display: flex;
  flex-direction: row;
  box-shadow: 0 5px 20px 0 rgba(25, 25, 25, 0.08);
  align-items: center;
  position: relative;
  justify-content: flex-start;
  padding: 0;

  //Mobile
  @media screen and (max-width: $mobile-max) {
    display: none;
  }

  //Tablet
  @media (min-width: $tablet-min) and (max-width: $tablet-max) {
    align-items: initial;
    box-shadow: initial;
    margin: calc(var(--spacer-base) + 20px) 50px 0 50px;
  }
}

.section-left {
  padding: 30px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  background: var(--c-white);
  width: 50%;

  //Tablet
  @media (min-width: $tablet-min) and (max-width: $tablet-max) {
    box-shadow: 0 5px 20px 0 rgba(25, 25, 25, 0.08);
    width: 50%;
    position: relative;
    z-index: 1;
  }
}

span {
  color: v-bind(topTitleColor);
  font-size: var(--font-size--sm);
  font-weight: bold;
  letter-spacing: 1px;
  text-transform: uppercase;
}

h2 {
  color: v-bind(titleColor);
  margin: 5px 0 10px 0;
  font-size: 30px;
  font-weight: bold;
  letter-spacing: 0.22px;
  line-height: 41px;

  //Tablet
  @media (min-width: $tablet-min) and (max-width: $tablet-max) {
    margin-top: 0;
    font-size: var(--h1-font-size);
    letter-spacing: initial;
    line-height: 33px;
  }
}

p {
  color: v-bind(subTextColor);
  margin: 0;
  font-size: var(--font-size--base);
  letter-spacing: 0.5px;
  line-height: 22px;
}

:deep(a) {
  color: v-bind(ctaTextColor);
  background: v-bind(buttonColor);
  border: 1px solid v-bind(borderColor);
  display: inline-flex;
  margin-top: 25px;
  padding: 10px 30px;
  border-radius: 3px;
  box-shadow: 0 5px 20px 0 rgba(25, 25, 25, 0.08);
  font-size: var(--font-size--sm);
  font-weight: bold;
  letter-spacing: 1px;
  text-transform: uppercase;
  text-decoration: none;
  text-align: center;
}

.section-right {
  width: 100%;
  text-align: right;
}

img {
  max-height: 395px;
  width: auto;
}
</style>
